<script setup>
import { defineAsyncComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFileUpload } from 'dashboard/composables/useFileUpload';
import { useIsMobile } from 'dashboard/composables/useMobileLayout';
import { vOnClickOutside } from '@vueuse/components';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useToggle } from '@vueuse/core';
import FileUpload from 'vue-upload-component';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import WhatsAppOptions from './WhatsAppOptions.vue';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import AIAssistButton from './AIAssistButton.vue';

const props = defineProps({
  isWhatsappInbox: { type: Boolean, default: false },
  isEmailOrWebWidgetInbox: { type: Boolean, default: false },
  isTwilioSmsInbox: { type: Boolean, default: false },
  messageTemplates: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  disableSendButton: { type: Boolean, default: false },
  hasSelectedInbox: { type: Boolean, default: false },
  hasNoInbox: { type: Boolean, default: false },
  isDropdownActive: { type: Boolean, default: false },
  messageSignature: { type: String, default: '' },
  disableSaveDraft: { type: Boolean, default: false },
  allowSignature: { type: Boolean, default: false },
  canResolveSourceConversation: { type: Boolean, default: false },
  message: { type: String, default: '' },
});

const emit = defineEmits([
  'sendMessage',
  'sendWhatsappMessage',
  'insertEmoji',
  'attachFile',
  'update:markResolved',
  'saveDraft',
  'sendAsInternal',
  'sendAndResolveOriginal',
  'toggleSignature',
  'replaceText',
  'aiAssistToggle',
  'aiModalToggle',
]);

const markResolved = defineModel('markResolved', {
  type: Boolean,
  default: true,
});

const { t } = useI18n();
const isMobile = useIsMobile();

const uploadAttachment = ref(null);
const isEmojiPickerOpen = ref(false);
const arrowDownButtonRef = ref(null);

const [showSendActionsDropdown, toggleSendDropdown] = useToggle();
const closeSendDropdown = () => toggleSendDropdown(false);
const openSendDropdown = () => toggleSendDropdown(true);

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

const { isEditorHotKeyEnabled } = useUISettings();

const toggleMessageSignature = () => emit('toggleSignature');

const onClickInsertEmoji = emoji => {
  emit('insertEmoji', emoji);
};

const { onFileUpload } = useFileUpload({
  isATwilioSMSChannel: props.isTwilioSmsInbox,
  attachFile: ({ blob, file }) => {
    emit('attachFile', { blob, file });
  },
});

const sendButtonLabel = computed(() => {
  // On phones there is no room (and no keyboard) for the shortcut hint
  if (isMobile.value) return t('CONVERSATION.REPLYBOX.SEND');
  const keyCode = isEditorHotKeyEnabled('cmd_enter') ? '⌘ + ↵' : '↵';
  return t('COMPOSE_NEW_CONVERSATION.FORM.ACTION_BUTTONS.SEND', {
    keyCode,
  });
});

const sendAndResolveOriginalLabel = computed(() =>
  t('COMPOSE_NEW_CONVERSATION.FORM.ACTION_BUTTONS.SEND_AND_RESOLVE_ORIGINAL', {
    keyCode: '⇧ + ⌘ + ↵',
  })
);

const canSend = computed(
  () => !props.isWhatsappInbox && !props.isLoading && !props.disableSendButton
);

const sendAndResolveOriginal = () => {
  if (!canSend.value || !props.canResolveSourceConversation) return;
  if (props.isDropdownActive) return;
  emit('sendAndResolveOriginal');
  closeSendDropdown();
};

const keyboardEvents = {
  Enter: {
    action: () => {
      if (
        isEditorHotKeyEnabled('enter') &&
        !props.isWhatsappInbox &&
        !props.isDropdownActive
      ) {
        emit('sendMessage');
      }
    },
  },
  '$mod+Enter': {
    action: () => {
      if (
        isEditorHotKeyEnabled('cmd_enter') &&
        !props.isWhatsappInbox &&
        !props.isDropdownActive
      ) {
        emit('sendMessage');
      }
    },
    allowOnFocusedInput: true,
  },
  '$mod+Shift+Enter': {
    action: sendAndResolveOriginal,
    allowOnFocusedInput: true,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div
    class="flex flex-wrap md:flex-nowrap items-center justify-between w-full min-h-[3.25rem] gap-2 px-4 py-3"
  >
    <div class="flex items-center gap-2">
      <WhatsAppOptions
        v-if="isWhatsappInbox"
        :message-templates="messageTemplates"
        @send-message="emit('sendWhatsappMessage', $event)"
      />
      <div
        v-if="!isWhatsappInbox && !hasNoInbox"
        v-on-click-outside="() => (isEmojiPickerOpen = false)"
        class="relative"
      >
        <Button
          icon="i-lucide-smile-plus"
          color="slate"
          size="sm"
          class="!w-10"
          @click="isEmojiPickerOpen = !isEmojiPickerOpen"
        />
        <EmojiInput
          v-if="isEmojiPickerOpen"
          class="left-0 top-full mt-1.5"
          :on-click="onClickInsertEmoji"
        />
      </div>
      <FileUpload
        v-if="isEmailOrWebWidgetInbox"
        ref="uploadAttachment"
        input-id="composeNewConversationAttachment"
        :size="4096 * 4096"
        :accept="ALLOWED_FILE_TYPES"
        multiple
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        class="p-px"
        @input-file="onFileUpload"
      >
        <Button
          icon="i-lucide-plus"
          color="slate"
          size="sm"
          class="!w-10 relative"
        />
      </FileUpload>
      <Button
        v-if="hasSelectedInbox && !isWhatsappInbox && messageSignature != ''"
        icon="i-ph-signature"
        :color="allowSignature ? 'blue' : 'slate'"
        :variant="allowSignature ? 'solid' : null"
        size="sm"
        class="!w-10"
        @click="toggleMessageSignature"
      />
      <AIAssistButton
        v-if="!isWhatsappInbox && !hasNoInbox"
        :message="message"
        @replace-text="emit('replaceText', $event)"
        @update:open="emit('aiAssistToggle', $event)"
        @update:modal-open="emit('aiModalToggle', $event)"
      />
    </div>

    <div class="flex items-center gap-2 max-md:ml-auto">
      <div class="flex items-center gap-2 mr-2">
        <Checkbox v-model="markResolved" />
        <label
          class="text-xs font-medium text-n-slate-11 cursor-pointer select-none"
          @click="markResolved = !markResolved"
        >
          {{ t('COMPOSE_NEW_CONVERSATION.FORM.ACTION_BUTTONS.MARK_RESOLVED') }}
        </label>
      </div>
      <Button
        :label="t('COMPOSE_NEW_CONVERSATION.FORM.ACTION_BUTTONS.SAVE_DRAFT')"
        variant="faded"
        color="slate"
        size="sm"
        class="!text-xs font-medium flex-shrink-0"
        :disabled="disableSaveDraft"
        @click="emit('saveDraft')"
      />
      <div v-if="!isWhatsappInbox" class="relative flex items-center">
        <div
          class="flex rounded-lg shadow outline-1 outline outline-n-container"
        >
          <Button
            :label="sendButtonLabel"
            size="sm"
            class="!text-xs font-medium ltr:rounded-r-none rtl:rounded-l-none !outline-0"
            :disabled="isLoading || disableSendButton"
            :is-loading="isLoading"
            @click="emit('sendMessage')"
          />
          <Button
            ref="arrowDownButtonRef"
            icon="i-lucide-chevron-down"
            :disabled="isLoading || disableSendButton"
            size="sm"
            class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
            color="blue"
            trailing-icon
            @click="openSendDropdown"
          />
        </div>
        <div
          v-if="showSendActionsDropdown"
          v-on-clickaway="closeSendDropdown"
          class="dropdown-pane dropdown-pane--open left-auto bottom-full mb-0.5 ltr:right-0 rtl:left-0"
        >
          <WootDropdownMenu class="mb-0">
            <WootDropdownItem v-if="canResolveSourceConversation">
              <Button
                :label="sendAndResolveOriginalLabel"
                ghost
                slate
                sm
                start
                icon="i-lucide-circle-check"
                class="!text-xs font-medium"
                @click="sendAndResolveOriginal"
              />
            </WootDropdownItem>
            <WootDropdownItem>
              <Button
                :label="
                  t(
                    'COMPOSE_NEW_CONVERSATION.FORM.ACTION_BUTTONS.CREATE_AS_INTERNAL'
                  )
                "
                ghost
                slate
                sm
                start
                icon="i-lucide-lock"
                class="!text-xs font-medium"
                @click="
                  () => {
                    emit('sendAsInternal');
                    closeSendDropdown();
                  }
                "
              />
            </WootDropdownItem>
          </WootDropdownMenu>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.emoji-dialog::before {
  @apply hidden;
}

// The <label> tag inside the file-upload component overlaps the button due to its position.
// This causes the button's hover state to not work, as it's positioned below the label (z-index).
// Increasing the button's z-index would break the file upload functionality.
// This style ensures the label remains clickable while preserving the button's hover effect.
:deep() {
  .file-uploads.file-uploads-html5 {
    label {
      @apply hover:cursor-pointer;
    }

    &:hover button {
      @apply dark:bg-n-solid-2 bg-n-alpha-2;
    }
  }
}

.dropdown-pane {
  .dropdown-menu__item {
    @apply mb-0;
  }
}
</style>
